import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

export interface ToolMenuEntry {
  id: string;
  visible: boolean;
}

interface ToolMenuConfig {
  pregnancy: ToolMenuEntry[];
  baby: ToolMenuEntry[];
}

let cachedConfig: ToolMenuConfig | null = null;
let cacheTimestamp = 0;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

export function useToolMenuConfig() {
  const [config, setConfig] = useState<ToolMenuConfig | null>(cachedConfig);
  const [isLoading, setIsLoading] = useState(!cachedConfig);

  const loadConfig = useCallback(async () => {
    if (cachedConfig && Date.now() - cacheTimestamp < CACHE_TTL) {
      setConfig(cachedConfig);
      setIsLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('system_settings')
        .select('key, value')
        .in('key', ['tool_menu_pregnancy', 'tool_menu_baby']);

      if (error) {
        console.error('Error loading tool menu config:', error);
        setIsLoading(false);
        return;
      }

      const configMap: Record<string, ToolMenuEntry[]> = {};
      (data as { key: string; value: unknown }[])?.forEach((s) => {
        configMap[s.key] = s.value as ToolMenuEntry[];
      });

      const loaded: ToolMenuConfig = {
        pregnancy: configMap['tool_menu_pregnancy'] || [],
        baby: configMap['tool_menu_baby'] || [],
      };

      cachedConfig = loaded;
      cacheTimestamp = Date.now();
      setConfig(loaded);
    } catch (err) {
      console.error('Error loading tool menu config:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  return { config, isLoading };
}
